import React from 'react';
import FormField from '../FormField';
import PhoneInput from '../PhoneInput';
import { CATALOGO_INICIATIVAS_PATH } from './networkingCatalogReturn';

const REL_EXTERNAL_TAB = 'noopener noreferrer';
export const DEFAULT_MAX_MANUAL_CONTACTS = 5;

export type ManualContactRow = {
  fullName: string;
  email: string;
  codigoPais: string;
  numeroTelefono: string;
  organization: string;
};

export function emptyManualContact(): ManualContactRow {
  return { fullName: '', email: '', codigoPais: 'AR', numeroTelefono: '', organization: '' };
}

export function isOptionalManualEmailOk(email: string): boolean {
  const e = email.trim();
  return !e || e.includes('@');
}

export function isManualRowComplete(c: ManualContactRow): boolean {
  return !!(c.fullName.trim() && c.organization.trim() && isOptionalManualEmailOk(c.email));
}

export function isManualRowPartial(c: ManualContactRow): boolean {
  const any = !!(
    c.fullName.trim() ||
    c.email.trim() ||
    c.numeroTelefono.trim() ||
    c.organization.trim()
  );
  return any && !isManualRowComplete(c);
}

interface ManualContactFieldsBlockProps {
  contacts: ManualContactRow[];
  setContacts: React.Dispatch<React.SetStateAction<ManualContactRow[]>>;
  fieldIdPrefix?: string;
  /** Por defecto 5; `null` = sin tope (admin). */
  maxContacts?: number | null;
  /** Si es false, no muestra el botón de agregar cuando la lista está vacía (admin: formulario inicial visible). */
  showAddButtonWhenEmpty?: boolean;
  /** No permite bajar de N filas al quitar (p. ej. 1 = siempre al menos una contraparte visible). */
  minContacts?: number;
  /** Teléfono compacto (perfil); en admin usar false para igualar al reservador. */
  compactPhone?: boolean;
  /** Clase extra para el botón de agregar contraparte. */
  addButtonClassName?: string;
}

export const ManualContactFieldsBlock: React.FC<ManualContactFieldsBlockProps> = ({
  contacts,
  setContacts,
  fieldIdPrefix = 'net-manual',
  maxContacts = DEFAULT_MAX_MANUAL_CONTACTS,
  showAddButtonWhenEmpty = true,
  minContacts = 0,
  compactPhone = true,
  addButtonClassName = '',
}) => {
  const canAddMore = maxContacts == null || contacts.length < maxContacts;
  const remaining =
    maxContacts == null ? null : Math.max(0, maxContacts - contacts.length);
  const showAddButton =
    canAddMore && (showAddButtonWhenEmpty || contacts.length > 0);
  const canRemove = contacts.length > minContacts;

  return (
    <>
      {contacts.length > 0 && (
        <div className="net-manual-partners">
          {contacts.map((row, idx) => (
            <div key={idx} className="net-manual-person-block">
              <p className="net-manual-person-title">Contraparte {idx + 1}</p>
              <FormField label="Organización" required>
                <input
                  id={`${fieldIdPrefix}-org-${idx}`}
                  className="form-input"
                  type="text"
                  autoComplete="organization"
                  value={row.organization}
                  onChange={(e) => {
                    const v = e.target.value;
                    setContacts((prev) => prev.map((r, i) => (i === idx ? { ...r, organization: v } : r)));
                  }}
                />
              </FormField>
              <FormField label="Nombre completo" required>
                <input
                  id={`${fieldIdPrefix}-fullname-${idx}`}
                  className="form-input"
                  type="text"
                  autoComplete="name"
                  value={row.fullName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setContacts((prev) => prev.map((r, i) => (i === idx ? { ...r, fullName: v } : r)));
                  }}
                />
              </FormField>
              <FormField label="Correo electrónico">
                <input
                  id={`${fieldIdPrefix}-email-${idx}`}
                  className="form-input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={row.email}
                  onChange={(e) => {
                    const v = e.target.value;
                    setContacts((prev) => prev.map((r, i) => (i === idx ? { ...r, email: v } : r)));
                  }}
                />
              </FormField>
              <FormField label="Teléfono">
                <PhoneInput
                  compact={compactPhone}
                  codigoPais={row.codigoPais}
                  numeroTelefono={row.numeroTelefono}
                  onCodigoPaisChange={(code) => {
                    setContacts((prev) => prev.map((r, i) => (i === idx ? { ...r, codigoPais: code } : r)));
                  }}
                  onNumeroTelefonoChange={(num) => {
                    setContacts((prev) => prev.map((r, i) => (i === idx ? { ...r, numeroTelefono: num } : r)));
                  }}
                />
              </FormField>
              {canRemove && (
                <div className="net-manual-person-remove">
                  <button
                    type="button"
                    className="logout-button net-btn-compact"
                    onClick={() => setContacts((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Quitar esta contraparte
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showAddButton && (
        <div className="net-add-persons-row">
          <button
            type="button"
            className={`save-profile-button net-add-person-btn${addButtonClassName ? ` ${addButtonClassName}` : ''}`}
            onClick={() =>
              setContacts((prev) =>
                maxContacts == null || prev.length < maxContacts ? [...prev, emptyManualContact()] : prev
              )
            }
          >
            {contacts.length === 0
              ? remaining == null
                ? 'Agregar datos de contraparte'
                : `Agregar datos de contraparte (${remaining} disponibles)`
              : remaining == null
                ? 'Agregar otra contraparte'
                : `Agregar otra contraparte (${remaining} disponibles)`}
          </button>
        </div>
      )}
    </>
  );
};

interface PartnerModalCommonFieldsProps {
  contacts: ManualContactRow[];
  setContacts: React.Dispatch<React.SetStateAction<ManualContactRow[]>>;
  fieldIdPrefix?: string;
  maxContacts?: number | null;
  onNavigateToCatalog?: () => void;
  onViewBusinessRoundsInterested?: () => void;
}

export const PartnerModalCommonFields: React.FC<PartnerModalCommonFieldsProps> = ({
  contacts,
  setContacts,
  fieldIdPrefix = 'net-manual',
  maxContacts = DEFAULT_MAX_MANUAL_CONTACTS,
  onNavigateToCatalog,
  onViewBusinessRoundsInterested,
}) => (
  <>
    <h3 className="net-subsection-title net-contraparte-heading">Contraparte</h3>
    <p className="net-muted net-muted--tight net-contraparte-lead">
      <strong>¿Qué es la contraparte?</strong> Son las personas u organizaciones con las que vas a tener la reunión en
      ese horario (no sos vos).
      {maxContacts != null
        ? ` Podés cargar hasta ${maxContacts} contrapartes con sus datos.`
        : ' Podés cargar todas las contrapartes que necesites.'}
    </p>
    <p className="net-muted net-muted--tight net-contraparte-lead">
      Si todavía no las tenés definidas, podés reservar el espacio igual y completar o editar estos datos después desde
      «Mis reuniones».
    </p>
    <p className="net-muted net-muted--tight net-contraparte-datoshint">
      <strong>Los campos de abajo son los datos de cada contraparte.</strong>
    </p>
    {onViewBusinessRoundsInterested ? (
      <p className="net-brr-interested-link-wrap">
        <button
          type="button"
          className="net-text-link-btn net-brr-interested-link"
          onClick={onViewBusinessRoundsInterested}
        >
          Ver participantes
        </button>
      </p>
    ) : null}
    <ManualContactFieldsBlock
      contacts={contacts}
      setContacts={setContacts}
      fieldIdPrefix={fieldIdPrefix}
      maxContacts={maxContacts}
    />
    <p className="net-muted net-muted--tight net-catalog-explainer">
      {onNavigateToCatalog ? (
        <button type="button" className="net-text-link-btn" onClick={onNavigateToCatalog}>
          Ver iniciativas del catálogo
        </button>
      ) : (
        <a
          href={CATALOGO_INICIATIVAS_PATH}
          target="_blank"
          rel={REL_EXTERNAL_TAB}
          className="net-text-link-btn"
        >
          Ver iniciativas del catálogo
        </a>
      )}{' '}
      (donde podés encontrar iniciativas de empresas, instituciones y gobiernos que pueden ser de tu interés para
      coordinar una reunión. Cabe aclarar que la coordinación de dicha reunión corre por cuenta de las partes y no de la
      organización.)
    </p>
  </>
);
